'use client';

import { useMemo } from 'react';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';

import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  XAxis,
  YAxis
} from 'recharts';

type ChartType = 'line' | 'bar' | 'area';

interface InteractiveGraphProps {
  data: any[] | string;
  config: string | {
    type?: ChartType;
    xAxisKey: string;
    yAxisKeys: { key: string; color?: string; name?: string }[];
    height?: number;
    title?: string;
    description?: string;
    tooltipFields?: string[];
    tooltipFieldsLabel?: string;
  };
}

const defaultColors = [
  '#10b981',
  '#066653',
  '#f59e0b',
  '#8b5cf6',
  '#35654D'
];

function fmt(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toFixed(1);
}

export function InteractiveGraph({
  data: rawData,
  config: rawConfig = {} as any
}: InteractiveGraphProps) {
  const data: any = Array.isArray(rawData)
    ? rawData
    : typeof rawData === 'string'
      ? JSON.parse(rawData)
      : [];
  const config: any =
    rawConfig && typeof rawConfig === 'object'
      ? rawConfig
      : typeof rawConfig === 'string'
        ? JSON.parse(rawConfig)
        : {};

  const {
    type = 'line',
    xAxisKey = 'name',
    yAxisKeys = [],
    height = 400,
    title,
    description,
    tooltipFields,
    tooltipFieldsLabel
  } = config;

  const coloredKeys = useMemo(
    () =>
      yAxisKeys.map((k: any, idx: number) => ({
        ...k,
        color: k.color || defaultColors[idx % defaultColors.length]
      })),
    [yAxisKeys]
  );

  const chartConfig = useMemo(() => {
    const config: any = {};
    coloredKeys.forEach((k: any) => {
      config[k.key] = {
        label: k.name || k.key,
        color: k.color
      };
    });
    return config;
  }, [coloredKeys]);

  const gradientDefs = useMemo(() => {
    if (type !== 'area') return null;
    return (
      <defs>
        {coloredKeys.map((k: any) => (
          <linearGradient
            key={k.key}
            id={`grad-${k.key}`}
            x1='0'
            y1='0'
            x2='0'
            y2='1'>
            <stop offset='0%' stopColor={k.color} stopOpacity={0.3} />
            <stop offset='100%' stopColor={k.color} stopOpacity={0.03} />
          </linearGradient>
        ))}
      </defs>
    );
  }, [coloredKeys, type]);

  const ChartComponent = type === 'bar' ? BarChart : type === 'area' ? ComposedChart : LineChart;
  const AnyTooltipContent: any = ChartTooltipContent;

  return (
    <div className='border-border bg-card relative my-8 w-full overflow-hidden rounded-xl border p-6 shadow-sm'>
      {(title || description) && (
        <div className='mb-6'>
          {title && (
            <h4 className='text-foreground font-serif text-xl font-bold'>
              {title}
            </h4>
          )}
          {description && (
            <p className='text-muted-foreground mt-1 text-sm italic'>{description}</p>
          )}
        </div>
      )}

      <div
        style={{ height: height, minHeight: height }}
        className='mt-4 w-full'>
        <ChartContainer
          config={chartConfig}
          className='h-full w-full'>
          <ChartComponent
            data={data}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid
              strokeDasharray='3 3'
              stroke='hsl(var(--border))'
              vertical={false}
            />
            <XAxis
              dataKey={xAxisKey}
              stroke='hsl(var(--muted-foreground))'
              fontSize={12}
              tickLine={false}
              axisLine={false}
              dy={10}
            />
            <YAxis
              stroke='hsl(var(--muted-foreground))'
              fontSize={12}
              tickLine={false}
              axisLine={false}
              dx={-8}
              tickFormatter={fmt}
            />
            <ChartTooltip
              content={(tp: any) => {
                if (!tp.active || !tp.payload?.length) return null;
                const row = tp.payload[0]?.payload;
                return (
                  <div>
                    <AnyTooltipContent {...tp} />
                    {tooltipFields && tooltipFields.length > 0 && row && (
                      <div className='border-border/50 border-t px-2.5 pb-1.5 pt-1.5 text-xs'>
                        {tooltipFieldsLabel && (
                          <p className='text-muted-foreground mb-0.5 font-medium'>
                            {tooltipFieldsLabel}
                          </p>
                        )}
                        {tooltipFields.map((f: string) => {
                          const v = row[f];
                          if (v == null) return null;
                          return (
                            <p
                              key={f}
                              className='text-muted-foreground leading-relaxed'>
                              {v}
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />

            {gradientDefs}

            {coloredKeys.map((k: any) => {
              if (type === 'bar') {
                return (
                  <Bar
                    key={k.key}
                    dataKey={k.key}
                    name={k.name || k.key}
                    fill={k.color}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={true}
                    animationDuration={800}
                    animationEasing='ease-out'
                    maxBarSize={48}
                  />
                );
              }
              if (type === 'area') {
                return (
                  <Area
                    key={k.key}
                    type='monotone'
                    dataKey={k.key}
                    name={k.name || k.key}
                    stroke={k.color}
                    strokeWidth={2.5}
                    fill={`url(#grad-${k.key})`}
                    dot={{ r: 3.5, strokeWidth: 1.5, stroke: k.color, fill: 'hsl(var(--background))' }}
                    activeDot={{ r: 5.5, strokeWidth: 0, fill: k.color }}
                    isAnimationActive={true}
                    animationDuration={1000}
                    animationEasing='ease-out'
                  />
                );
              }
              return (
                <Line
                  key={k.key}
                  type='monotone'
                  dataKey={k.key}
                  name={k.name || k.key}
                  stroke={k.color}
                  strokeWidth={2.5}
                  dot={{ r: 3.5, strokeWidth: 1.5, stroke: k.color, fill: 'hsl(var(--background))' }}
                  activeDot={{ r: 5.5, strokeWidth: 0, fill: k.color }}
                  isAnimationActive={true}
                  animationDuration={1000}
                  animationEasing='ease-out'
                />
              );
            })}
          </ChartComponent>
        </ChartContainer>
      </div>
    </div>
  );
}
